// Civil-time → instant helpers that src/core/time.ts does not (yet) offer: the inverse of
// `civil()`. They are the web's `Calendar.date(from: DateComponents)`, `startOfDay(for:)` and
// `date(byAdding: .day, …)` with an explicit `timeZone`, written once for searchQuery.ts and
// scheduleOptions.ts (the compose/send/search port). Candidates to fold into time.ts.
import { civil } from './time';

/** The zone's UTC offset (ms, east positive) at instant `ms`, to the second. */
function offsetAt(ms: number, timeZone: string | undefined): number {
  const whole = Math.floor(ms / 1000) * 1000;
  const c = civil(whole, timeZone);
  return Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute, c.second) - whole;
}

/**
 * The instant at which the wall clock in `timeZone` reads `year-month-day hour:minute:second`.
 * Out-of-range fields roll over the way Foundation's `Calendar.date(from:)` does (Feb 31 → Mar 3;
 * day 0 → the last day of the month before).
 *
 * A wall time that does not exist (inside a spring-forward gap) resolves to the instant AFTER the
 * gap, as Foundation does; a wall time that happens twice resolves to one of the two (Mexico City
 * has kept no DST since 2022, so neither case is reachable for this workspace's own zone).
 */
export function epochForCivil(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
  timeZone?: string,
): number {
  const wall = Date.UTC(year, month - 1, day, hour, minute, second);
  const o1 = offsetAt(wall, timeZone);
  const t1 = wall - o1;
  const o2 = offsetAt(t1, timeZone);
  if (o1 === o2) return t1;
  const t2 = wall - o2;
  const o3 = offsetAt(t2, timeZone);
  if (o3 === o2) return t2;
  // In a gap: neither offset reproduces the wall time. Foundation moves forward past it.
  return Math.max(t1, t2);
}

/** Midnight starting `ms`'s calendar day in `timeZone` (`Calendar.startOfDay(for:)`). */
export function startOfDay(ms: number, timeZone?: string): number {
  const c = civil(ms, timeZone);
  return epochForCivil(c.year, c.month, c.day, 0, 0, 0, timeZone);
}

/**
 * `ms` moved by `days` CALENDAR days at the same wall-clock time (`Calendar.date(byAdding: .day,
 * value:, to:)`) — never by adding 86,400,000, because a DST day is 23 or 25 hours long.
 */
export function addingDays(ms: number, days: number, timeZone?: string): number {
  const c = civil(ms, timeZone);
  const subSecond = ms - Math.floor(ms / 1000) * 1000;
  return epochForCivil(c.year, c.month, c.day + days, c.hour, c.minute, c.second, timeZone) + subSecond;
}
